import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SafeHtml } from '@angular/platform-browser';
import { ServiceBase } from '../service-base';

export interface Message {
    text: SafeHtml | string;
    markdown?: string;
    isUser: boolean;
    annotations?: any;
    thread?: string;
    complete: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ChatStateService implements ServiceBase {
    private _messages$ = new BehaviorSubject<Message[]>([]);
    private _thread$ = new BehaviorSubject<string>('');
    private _loading$ = new BehaviorSubject<boolean>(false);
    private _waiting$ = new BehaviorSubject<boolean>(false);
    private _isChatStarted$ = new BehaviorSubject<boolean>(false);
    private _messageQueue$ = new BehaviorSubject<string[]>([]);

    // Expose as observables
    messages$ = this._messages$.asObservable();
    thread$ = this._thread$.asObservable();
    loading$ = this._loading$.asObservable();
    waiting$ = this._waiting$.asObservable();
    isChatStarted$ = this._isChatStarted$.asObservable();
    messageQueue$ = this._messageQueue$.asObservable();

    // Getters for current values
    get messages(): Message[] {
        return this._messages$.value;
    }

    get thread(): string {
        return this._thread$.value;
    }

    get loading(): boolean {
        return this._loading$.value;
    }

    get waiting(): boolean {
        return this._waiting$.value;
    }

    get isChatStarted(): boolean {
        return this._isChatStarted$.value;
    }

    get messageQueue(): string[] {
        return this._messageQueue$.value;
    }

    // Setters for updating state
    setMessages(messages: Message[]): void {
        this._messages$.next(messages);
    }

    addMessage(message: Message): void {
        this._messages$.next([...this._messages$.value, message]);
    }

    setThread(thread: string): void {
        this._thread$.next(thread);
    }

    setLoading(loading: boolean): void {
        this._loading$.next(loading);
    }

    setWaiting(waiting: boolean): void {
        this._waiting$.next(waiting);
    }

    setIsChatStarted(started: boolean): void {
        this._isChatStarted$.next(started);
    }

    addToQueue(message: string): void {
        this._messageQueue$.next([...this._messageQueue$.value, message]);
    }

    shiftFromQueue(): string | undefined {
        const queue = [...this._messageQueue$.value];
        const message = queue.shift();
        this._messageQueue$.next(queue);
        return message;
    }

    resetChat(): void {
        this._messages$.next([]);
        this._thread$.next('');
        this._loading$.next(false);
        this._waiting$.next(false);
        this._messageQueue$.next([]);
        // Keep isChatStarted as true so the overlay stays open
    }

    clearCache(): void {
        this._messages$.next([]);
        this._thread$.next('');
        this._loading$.next(false);
        this._waiting$.next(false);
        this._isChatStarted$.next(false);
        this._messageQueue$.next([]);
    }
}
